/**
 * Seção Desempenho do Relatório
 * KPIs + Gráficos de barras por fornecedor
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { cn } from '@/lib/utils';
import { Users, FileText, Briefcase, BarChart3, CheckCircle2, AlertCircle, Clock, Plus, Pencil, Trash2, FolderPlus } from 'lucide-react';
import { useDesempenho, useRelatoriosFilters, useRegionals, type SupplierData, type SupplierGroup, GROUP_COLORS } from '../hooks';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';

// ============================================================================
// HOOKS
// ============================================================================

function useIsDarkMode() {
  const [isDark, setIsDark] = useState(() => 
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    
    return () => observer.disconnect();
  }, []);

  return isDark;
}

// ============================================================================
// SUPPLIER GROUP MANAGER DIALOG
// ============================================================================

interface SupplierGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableSuppliers: string[];
  editingGroup?: SupplierGroup | null;
}

function SupplierGroupDialog({ open, onOpenChange, availableSuppliers, editingGroup }: SupplierGroupDialogProps) {
  const { addSupplierGroup, updateSupplierGroup, deleteSupplierGroup, supplierGroups } = useRelatoriosFilters();
  
  const [groupName, setGroupName] = useState('');
  const [selectedSuppliers, setSelectedSuppliers] = useState<Set<string>>(new Set());
  const [selectedColor, setSelectedColor] = useState(GROUP_COLORS[0]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Inicializa estado quando edita um grupo existente
  useEffect(() => {
    if (editingGroup) {
      setGroupName(editingGroup.name);
      setSelectedSuppliers(new Set(editingGroup.suppliers));
      setSelectedColor(editingGroup.color || GROUP_COLORS[0]);
    } else {
      setGroupName('');
      setSelectedSuppliers(new Set());
      setSelectedColor(GROUP_COLORS[supplierGroups.length % GROUP_COLORS.length]);
    }
    setSearchTerm('');
  }, [editingGroup, open, supplierGroups.length]);
  
  // Filtra fornecedores pela busca
  const filteredSuppliers = useMemo(() => {
    if (!searchTerm) return availableSuppliers;
    const term = searchTerm.toLowerCase();
    return availableSuppliers.filter(s => s.toLowerCase().includes(term));
  }, [availableSuppliers, searchTerm]);
  
  const toggleSupplier = (supplier: string) => {
    const newSet = new Set(selectedSuppliers);
    if (newSet.has(supplier)) {
      newSet.delete(supplier);
    } else {
      newSet.add(supplier);
    }
    setSelectedSuppliers(newSet);
  };
  
  const handleSave = () => {
    if (!groupName.trim() || selectedSuppliers.size === 0) return;
    
    if (editingGroup) {
      updateSupplierGroup(editingGroup.id, {
        name: groupName.trim(),
        suppliers: Array.from(selectedSuppliers),
        color: selectedColor,
      });
    } else {
      addSupplierGroup({
        name: groupName.trim(),
        suppliers: Array.from(selectedSuppliers),
        color: selectedColor,
      });
    }
    
    onOpenChange(false);
  };
  
  const handleDelete = () => {
    if (editingGroup && confirm(`Excluir o grupo "${editingGroup.name}"?`)) {
      deleteSupplierGroup(editingGroup.id);
      onOpenChange(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{editingGroup ? 'Editar Grupo' : 'Criar Grupo de Fornecedores'}</DialogTitle>
          <DialogDescription>
            Agrupe fornecedores para visualização consolidada no gráfico.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Nome do grupo */}
          <div className="space-y-2">
            <Label htmlFor="group-name">Nome do Grupo</Label>
            <Input
              id="group-name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Ex: Equipes Externas SP"
            />
          </div>
          
          {/* Cor do grupo */}
          <div className="space-y-2">
            <Label>Cor</Label>
            <div className="flex gap-2">
              {GROUP_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  className={cn(
                    'w-8 h-8 rounded-full border-2 transition-all',
                    selectedColor === color ? 'border-foreground scale-110' : 'border-transparent'
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
          
          {/* Seleção de fornecedores */}
          <div className="space-y-2">
            <Label>Fornecedores ({selectedSuppliers.size} selecionados)</Label>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar fornecedor..."
              className="mb-2"
            />
            <ScrollArea className="h-[200px] border rounded-md p-2">
              <div className="space-y-1">
                {filteredSuppliers.map((supplier) => (
                  <label
                    key={supplier}
                    className={cn(
                      'flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors',
                      selectedSuppliers.has(supplier) && 'bg-primary/10'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSuppliers.has(supplier)}
                      onChange={() => toggleSupplier(supplier)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">{supplier}</span>
                  </label>
                ))}
                {filteredSuppliers.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    Nenhum fornecedor encontrado
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          {editingGroup && (
            <Button variant="destructive" onClick={handleDelete} className="mr-auto">
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!groupName.trim() || selectedSuppliers.size === 0}>
            {editingGroup ? 'Salvar' : 'Criar Grupo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// SUPPLIER VIEW MODE SELECTOR
// ============================================================================

interface SupplierViewModeSelectorProps {
  availableSuppliers: string[];
}

function SupplierViewModeSelector({ availableSuppliers }: SupplierViewModeSelectorProps) {
  const { supplierViewMode, setSupplierViewMode, supplierGroups } = useRelatoriosFilters();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SupplierGroup | null>(null);
  
  const openCreateDialog = () => {
    setEditingGroup(null);
    setDialogOpen(true);
  };
  
  const openEditDialog = (group: SupplierGroup) => {
    setEditingGroup(group);
    setDialogOpen(true);
  };
  
  return (
    <div className="space-y-3">
      {/* Modo de visualização */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-muted-foreground">Visualização:</span>
        <div className="flex gap-1">
          <button
            onClick={() => setSupplierViewMode('todos')}
            className={cn(
              'px-3 py-1.5 text-sm rounded-md border transition-colors',
              supplierViewMode === 'todos'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
            )}
          >
            Todos
          </button>
          <button
            onClick={() => setSupplierViewMode('equipes')}
            className={cn(
              'px-3 py-1.5 text-sm rounded-md border transition-colors',
              supplierViewMode === 'equipes'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
            )}
          >
            Equipes
          </button>
          <button
            onClick={() => setSupplierViewMode('customizado')}
            disabled={supplierGroups.length === 0}
            className={cn(
              'px-3 py-1.5 text-sm rounded-md border transition-colors',
              supplierViewMode === 'customizado'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted',
              supplierGroups.length === 0 && 'opacity-50 cursor-not-allowed'
            )}
          >
            Customizado
          </button>
        </div>
        
        {/* Botão criar grupo */}
        <button
          onClick={openCreateDialog}
          className="ml-2 flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-dashed border-primary/50 text-primary hover:bg-primary/10 transition-colors"
        >
          <FolderPlus className="h-4 w-4" />
          Criar Grupo
        </button>
      </div>
      
      {/* Lista de grupos existentes */}
      {supplierGroups.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Grupos:</span>
          {supplierGroups.map((group) => (
            <button
              key={group.id}
              onClick={() => openEditDialog(group)}
              className="flex items-center gap-1.5 px-2 py-1 text-xs rounded-md border bg-muted/50 hover:bg-muted transition-colors"
            >
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: group.color }}
              />
              <span>{group.name}</span>
              <span className="text-muted-foreground">({group.suppliers.length})</span>
              <Pencil className="h-3 w-3 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}
      
      {/* Dialog */}
      <SupplierGroupDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        availableSuppliers={availableSuppliers}
        editingGroup={editingGroup}
      />
    </div>
  );
}

// ============================================================================
// KPI CARD
// ============================================================================

interface KpiCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: 'default' | 'blue' | 'purple' | 'green' | 'amber' | 'red' | 'cyan';
  isLoading?: boolean;
}

function KpiCard({ title, value, icon, color, isLoading }: KpiCardProps) {
  const colorClasses = {
    default: '',
    blue: 'border-blue-500/30 bg-blue-50/50 dark:bg-blue-950/20',
    purple: 'border-purple-500/30 bg-purple-50/50 dark:bg-purple-950/20',
    green: 'border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20',
    amber: 'border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20',
    red: 'border-red-500/30 bg-red-50/50 dark:bg-red-950/20',
    cyan: 'border-cyan-500/30 bg-cyan-50/50 dark:bg-cyan-950/20',
  };

  const textColors = {
    default: 'text-foreground',
    blue: 'text-blue-600 dark:text-blue-400',
    purple: 'text-purple-600 dark:text-purple-400',
    green: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
    cyan: 'text-cyan-600 dark:text-cyan-400',
  };

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg border p-4 animate-pulse">
        <div className="h-4 bg-muted rounded w-24 mb-2" />
        <div className="h-8 bg-muted rounded w-16" />
      </div>
    );
  }

  return (
    <div className={cn('bg-card rounded-lg border p-4', colorClasses[color])}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
        <span className={textColors[color]}>{icon}</span>
      </div>
      <div className={cn('text-3xl font-bold', textColors[color])}>
        {typeof value === 'number' ? value.toLocaleString('pt-BR') : value}
      </div>
    </div>
  );
}

// ============================================================================
// FILTROS
// ============================================================================

interface FiltersProps {
  docTypes: string[];
  suppliers: string[];          // Lista de nomes de fornecedores disponíveis
  periods: string[];            // Períodos consultados
  availablePeriods: string[];   // Períodos disponíveis nos dados (para os filtros)
  isLoading?: boolean;
}

// Gera lista de meses (01-12)
const MONTH_OPTIONS = [
  { value: '01', label: 'Jan' },
  { value: '02', label: 'Fev' },
  { value: '03', label: 'Mar' },
  { value: '04', label: 'Abr' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Jun' },
  { value: '07', label: 'Jul' },
  { value: '08', label: 'Ago' },
  { value: '09', label: 'Set' },
  { value: '10', label: 'Out' },
  { value: '11', label: 'Nov' },
  { value: '12', label: 'Dez' },
];

// Opções de status
const STATUS_OPTIONS = [
  { value: 'concluido', label: 'Concluído' },
  { value: 'atrasado', label: 'Atrasado' },
  { value: 'andamento', label: 'Em Andamento' },
];

function Filters({ docTypes, suppliers, periods, availablePeriods, isLoading }: FiltersProps) {
  const { 
    selectedRegionals,
    selectedYears,
    selectedMonths,
    selectedDocTypes,
    selectedSuppliers,
    selectedStatus,
    setSelectedRegionals, 
    setSelectedYears,
    setSelectedMonths,
    setSelectedDocTypes,
    setSelectedSuppliers,
    setSelectedStatus,
  } = useRelatoriosFilters();
  
  const { data: regionals } = useRegionals();
  
  // Opções de regionais
  const regionalOptions = useMemo(() => 
    (regionals || []).map((r: string) => ({ value: r, label: r })),
    [regionals]
  );
  
  // Opções de anos - extrai dos períodos disponíveis
  const yearOptions = useMemo(() => {
    const years = new Set<string>();
    for (const period of availablePeriods) {
      const year = period.split('-')[0];
      if (year) years.add(year);
    }
    return Array.from(years)
      .sort((a, b) => b.localeCompare(a)) // Mais recente primeiro
      .map(y => ({ value: y, label: y }));
  }, [availablePeriods]);
  
  // Opções de meses - extrai dos períodos disponíveis para os anos selecionados
  const monthOptions = useMemo(() => {
    const months = new Set<string>();
    const targetYears = selectedYears.size > 0 ? selectedYears : new Set(yearOptions.map(y => y.value));
    
    for (const period of availablePeriods) {
      const [year, month] = period.split('-');
      if (year && month && targetYears.has(year)) {
        months.add(month);
      }
    }
    
    return MONTH_OPTIONS.filter(m => months.has(m.value));
  }, [availablePeriods, selectedYears, yearOptions]);
  
  // Opções de tipos de documento (da API)
  const docTypeOptions = useMemo(() => 
    docTypes.map(dt => ({ value: dt, label: dt })),
    [docTypes]
  );
  
  // Opções de fornecedores (da API)
  const supplierOptions = useMemo(() => 
    suppliers.map(s => ({ value: s, label: s })),
    [suppliers]
  );

  return (
    <div className="flex flex-wrap gap-4 p-4 bg-muted/30 rounded-lg border items-end">
      {/* Regional (MultiSelect) */}
      <div className="min-w-[150px]">
        <MultiSelect
          label="Regional"
          options={regionalOptions}
          selected={selectedRegionals}
          onChange={setSelectedRegionals}
          placeholder="Selecione..."
          allLabel="Todas"
        />
      </div>

      {/* Ano (MultiSelect) */}
      <div className="min-w-[120px]">
        <MultiSelect
          label="Ano"
          options={yearOptions}
          selected={selectedYears}
          onChange={setSelectedYears}
          placeholder="Selecione..."
          allLabel="Todos"
        />
      </div>

      {/* Meses (MultiSelect) */}
      <div className="min-w-[150px]">
        <MultiSelect
          label="Meses"
          options={monthOptions}
          selected={selectedMonths}
          onChange={setSelectedMonths}
          placeholder="Selecione..."
          allLabel="Todos"
        />
      </div>

      {/* Status (MultiSelect) */}
      <div className="min-w-[150px]">
        <MultiSelect
          label="Status"
          options={STATUS_OPTIONS}
          selected={selectedStatus as Set<string>}
          onChange={(s) => setSelectedStatus(s as Set<'concluido' | 'andamento' | 'atrasado'>)}
          placeholder="Selecione..."
          allLabel="Todos"
        />
      </div>

      {/* Tipo de Documento (MultiSelect) */}
      <div className="min-w-[200px]">
        <MultiSelect
          label="Tipo de Documento"
          options={docTypeOptions}
          selected={selectedDocTypes}
          onChange={setSelectedDocTypes}
          placeholder="Selecione..."
          allLabel="Todos"
        />
      </div>

      {/* Fornecedores (MultiSelect) */}
      <div className="min-w-[200px]">
        <MultiSelect
          label="Fornecedores"
          options={supplierOptions}
          selected={selectedSuppliers}
          onChange={setSelectedSuppliers}
          placeholder="Selecione..."
          allLabel="Todos"
        />
      </div>

      {/* Info de períodos */}
      {periods.length > 0 && (
        <div className="flex items-center text-xs text-muted-foreground ml-auto">
          <span>Período: {periods.join(', ')}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COMPONENTE MULTI-SELECT DROPDOWN
// ============================================================================

interface MultiSelectProps {
  label: string;
  options: { value: string; label: string }[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
  placeholder?: string;
  allLabel?: string;
}

function MultiSelect({ label, options, selected, onChange, placeholder = 'Selecione...', allLabel = 'Todos' }: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value: string) => {
    const newSet = new Set(selected);
    if (newSet.has(value)) {
      newSet.delete(value);
    } else {
      newSet.add(value);
    }
    onChange(newSet);
  };

  const selectAll = () => {
    onChange(new Set(options.map(o => o.value)));
  };

  const clearAll = () => {
    onChange(new Set());
  };

  const displayText = selected.size === 0 
    ? allLabel
    : selected.size === options.length 
      ? allLabel
      : selected.size === 1 
        ? options.find(o => o.value === Array.from(selected)[0])?.label || '1 selecionado'
        : `${selected.size} selecionados`;

  return (
    <div className="flex flex-col gap-1" ref={dropdownRef}>
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-3 py-1.5 text-sm border rounded-md bg-background text-left flex items-center justify-between gap-2 hover:border-primary/50"
        >
          <span className="truncate">{displayText}</span>
          <svg 
            className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
            {/* Ações rápidas */}
            <div className="flex gap-2 p-2 border-b bg-muted/30">
              <button
                type="button"
                onClick={selectAll}
                className="text-xs text-primary hover:underline"
              >
                Selecionar todos
              </button>
              <span className="text-muted-foreground">|</span>
              <button
                type="button"
                onClick={clearAll}
                className="text-xs text-primary hover:underline"
              >
                Limpar
              </button>
            </div>

            {/* Opções */}
            <div className="p-1">
              {options.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(option.value)}
                    onChange={() => toggleOption(option.value)}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm truncate">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// GRÁFICO 1: PROCESSOS POR FORNECEDOR (POR STATUS)
// ============================================================================

interface PerformanceChartProps {
  isLoading?: boolean;
}

// Cores e nomes para cada status
const STATUS_CHART_CONFIG = {
  concluido: { color: '#10b981', name: 'Concluídos' },    // Verde
  atrasado: { color: '#ef4444', name: 'Atrasados' },      // Vermelho
  andamento: { color: '#06b6d4', name: 'Em Andamento' },  // Ciano
} as const;

function PerformanceChart({ isLoading: parentLoading }: PerformanceChartProps) {
  const isDark = useIsDarkMode();
  const textColor = isDark ? '#e5e7eb' : '#374151';
  
  // Usa filtros globais da section (via hook), incluindo fornecedores e status
  const { selectedSuppliers, selectedStatus, supplierViewMode, supplierGroups } = useRelatoriosFilters();
  const { data: desempenhoData, isLoading: queryLoading } = useDesempenho();
  const data = desempenhoData?.bySupplier || [];
  const isLoading = parentLoading || queryLoading;

  // Processa dados aplicando agrupamento se modo customizado
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Se modo customizado e há grupos, agrupa fornecedores
    if (supplierViewMode === 'customizado' && supplierGroups.length > 0) {
      // Mapa de fornecedor -> grupo
      const supplierToGroup = new Map<string, SupplierGroup>();
      for (const group of supplierGroups) {
        for (const supplier of group.suppliers) {
          supplierToGroup.set(supplier, group);
        }
      }
      
      // Agrega dados por grupo
      const groupedData = new Map<string, { name: string; total: number; concluido: number; andamento: number; atrasado: number; isGroup: boolean; color?: string }>();
      const ungroupedData: typeof data = [];
      
      for (const supplier of data) {
        const group = supplierToGroup.get(supplier.name);
        
        if (group) {
          // Fornecedor pertence a um grupo
          if (!groupedData.has(group.id)) {
            groupedData.set(group.id, {
              name: group.name,
              total: 0,
              concluido: 0,
              andamento: 0,
              atrasado: 0,
              isGroup: true,
              color: group.color,
            });
          }
          const gd = groupedData.get(group.id)!;
          gd.total += supplier.total;
          gd.concluido += supplier.concluido;
          gd.andamento += supplier.andamento;
          gd.atrasado += supplier.atrasado;
        } else {
          // Fornecedor não pertence a nenhum grupo
          ungroupedData.push(supplier);
        }
      }
      
      // Combina grupos + fornecedores não agrupados
      const result = [
        ...Array.from(groupedData.values()),
        ...ungroupedData.map(s => ({ ...s, isGroup: false })),
      ];
      
      return result.sort((a, b) => b.total - a.total);
    }
    
    return data.map(s => ({ ...s, isGroup: false }));
  }, [data, supplierViewMode, supplierGroups]);

  // Dados filtrados - mostra todos para permitir comparação (inclusive com valor 0)
  const filteredData = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];
    
    // Filtra por fornecedor se houver seleção específica
    if (selectedSuppliers.size > 0) {
      return processedData.filter(d => selectedSuppliers.has(d.name));
    }
    
    // Se nenhum selecionado, mostra todos os fornecedores para comparação
    return processedData;
  }, [processedData, selectedSuppliers]);

  // Status selecionados como array para iterar
  const selectedStatusArray = useMemo(() => 
    Array.from(selectedStatus) as Array<'concluido' | 'atrasado' | 'andamento'>,
    [selectedStatus]
  );

  const option: EChartsOption = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return {};

    // Marca grupos com prefixo para destaque visual
    const categories = filteredData.map((d) => 
      d.isGroup ? `📁 ${d.name}` : d.name
    );
    
    // Gera uma série para cada status selecionado
    const series = selectedStatusArray.map((statusKey, idx) => {
      const config = STATUS_CHART_CONFIG[statusKey];
      const values = filteredData.map((d) => d[statusKey] || 0);
      
      return {
        name: config.name,
        type: 'bar' as const,
        data: values,
        itemStyle: { 
          color: config.color,
          borderRadius: selectedStatusArray.length === 1 
            ? [0, 4, 4, 0]  // Só uma barra: arredonda direita
            : idx === selectedStatusArray.length - 1 
              ? [0, 4, 4, 0]  // Última barra: arredonda direita
              : [0, 0, 0, 0], // Barras intermediárias: sem arredondamento
        },
        label: {
          show: selectedStatusArray.length === 1, // Mostra label só se for uma série
          position: 'right' as const,
          color: textColor,
          fontSize: 10,
          formatter: '{c}',
        },
      };
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const paramsArray = params as Array<{
            name: string;
            marker: string;
            seriesName: string;
            value: number;
          }>;
          if (!paramsArray.length) return '';
          
          const name = paramsArray[0].name;
          let html = `<strong>${name}</strong><br/>`;
          let total = 0;
          
          for (const p of paramsArray) {
            html += `${p.marker} ${p.seriesName}: <strong>${p.value.toLocaleString('pt-BR')}</strong><br/>`;
            total += p.value;
          }
          
          if (paramsArray.length > 1) {
            html += `<br/><strong>Total: ${total.toLocaleString('pt-BR')}</strong>`;
          }
          
          return html;
        },
      },
      legend: {
        top: 0,
        textStyle: { color: textColor },
        data: selectedStatusArray.map(s => STATUS_CHART_CONFIG[s].name),
      },
      grid: {
        left: 12,
        right: 24,
        top: 40,
        bottom: 12,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: { color: textColor },
        splitLine: {
          lineStyle: { color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
        },
      },
      yAxis: {
        type: 'category',
        data: categories,
        inverse: true,
        axisLabel: {
          color: textColor,
          width: 150,
          overflow: 'truncate',
          ellipsis: '...',
        },
      },
      series,
    };
  }, [filteredData, textColor, isDark, selectedStatusArray]);

  // Altura dinâmica: 35px por fornecedor + padding
  const chartHeight = Math.max(350, Math.min(700, filteredData.length * 35 + 80));

  // Renderiza conteúdo do gráfico (loading, vazio ou gráfico)
  const renderChartContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-[350px]">
          <div className="animate-pulse text-muted-foreground">Carregando dados do PropertyDocs...</div>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-[350px] text-muted-foreground">
          <span>Sem dados disponíveis para os filtros selecionados</span>
          <span className="text-xs mt-2">Tente ajustar os filtros na seção acima</span>
        </div>
      );
    }

    return (
      <div data-pdf-block>
        <ReactECharts
          option={option}
          style={{ height: `${chartHeight}px`, width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      </div>
    );
  };

  return (
    <div>
      {/* Info */}
      <div className="text-xs text-muted-foreground mb-2">
        {filteredData.length} fornecedor(es) exibidos
      </div>

      {/* Gráfico */}
      {renderChartContent()}
    </div>
  );
}

// ============================================================================
// GRÁFICO 2: DOCUMENTOS POR FORNECEDOR (AVCB/ALVARÁ)
// ============================================================================

interface SupplierStatusChartProps {
  data: SupplierData[];
  isLoading?: boolean;
}

// Cores para tipos de documento
const DOC_TYPE_COLORS: Record<string, string> = {
  'avcb': '#3b82f6',     // Azul
  'alvara': '#10b981',   // Verde
  'default': '#8b5cf6',  // Roxo
};

// Tipos de documento padrão
const DEFAULT_DOC_TYPES = ['avcb', 'alvara'];

// Helper para normalizar nome do tipo de documento
function normalizeDocType(type: string): string {
  const t = type.toLowerCase();
  if (t.includes('avcb') || t.includes('clcb') || t.includes('bombeiro')) return 'avcb';
  if (t.includes('alvara') || t.includes('alvará')) return 'alvara';
  return type;
}

// Helper para label amigável
function getDocTypeLabel(type: string): string {
  if (type === 'avcb') return 'AVCB/CLCB';
  if (type === 'alvara') return 'Alvará';
  return type;
}

function SupplierDocTypeChart({ data, isLoading }: SupplierStatusChartProps) {
  const isDark = useIsDarkMode();
  const textColor = isDark ? '#e5e7eb' : '#374151';
  
  // Usa filtros globais
  const { selectedSuppliers, selectedStatus, supplierViewMode, supplierGroups } = useRelatoriosFilters();
  
  // Estado local para tipos de documento selecionados
  const [selectedDocTypes, setSelectedDocTypes] = useState<Set<string>>(new Set(DEFAULT_DOC_TYPES));
  
  // Lista de tipos de documento disponíveis (normalizados)
  const availableDocTypes = useMemo(() => {
    const types = new Set<string>();
    for (const supplier of data || []) {
      for (const dt of supplier.byDocType || []) {
        types.add(normalizeDocType(dt.type));
      }
    }
    return Array.from(types).sort();
  }, [data]);
  
  // Processa dados aplicando agrupamento se modo customizado
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Se modo customizado e há grupos, agrupa fornecedores
    if (supplierViewMode === 'customizado' && supplierGroups.length > 0) {
      // Mapa de fornecedor -> grupo
      const supplierToGroup = new Map<string, SupplierGroup>();
      for (const group of supplierGroups) {
        for (const supplier of group.suppliers) {
          supplierToGroup.set(supplier, group);
        }
      }
      
      // Agrega dados por grupo
      const groupedData = new Map<string, SupplierData & { isGroup: boolean }>();
      const ungroupedData: (SupplierData & { isGroup: boolean })[] = [];
      
      for (const supplier of data) {
        const group = supplierToGroup.get(supplier.name);
        
        if (group) {
          // Fornecedor pertence a um grupo
          if (!groupedData.has(group.id)) {
            groupedData.set(group.id, {
              name: group.name,
              total: 0,
              concluido: 0,
              andamento: 0,
              atrasado: 0,
              regular: 0,
              irregular: 0,
              byDocType: [],
              isGroup: true,
            });
          }
          const gd = groupedData.get(group.id)!;
          gd.total += supplier.total;
          gd.concluido += supplier.concluido;
          gd.andamento += supplier.andamento;
          gd.atrasado += supplier.atrasado;
          // Merge byDocType
          for (const dt of supplier.byDocType || []) {
            const existing = gd.byDocType.find(d => d.type === dt.type);
            if (existing) {
              existing.concluido += dt.concluido;
              existing.andamento += dt.andamento;
              existing.atrasado += dt.atrasado;
            } else {
              gd.byDocType.push({ ...dt });
            }
          }
        } else {
          // Fornecedor não pertence a nenhum grupo
          ungroupedData.push({ ...supplier, isGroup: false });
        }
      }
      
      // Combina grupos + fornecedores não agrupados
      const result = [
        ...Array.from(groupedData.values()),
        ...ungroupedData,
      ];
      
      return result.sort((a, b) => b.total - a.total);
    }
    
    return data.map(s => ({ ...s, isGroup: false }));
  }, [data, supplierViewMode, supplierGroups]);
  
  // Aplica filtro de fornecedores
  const filteredData = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];
    
    if (selectedSuppliers.size > 0) {
      return processedData.filter(d => selectedSuppliers.has(d.name));
    }
    
    return processedData;
  }, [processedData, selectedSuppliers]);

  // Status selecionados como array
  const selectedStatusArray = useMemo(() => 
    Array.from(selectedStatus) as Array<'concluido' | 'atrasado' | 'andamento'>,
    [selectedStatus]
  );

  // Agrega por fornecedor e tipo de documento normalizado
  const chartData = useMemo(() => {
    const supplierDocMap = new Map<string, Map<string, number>>();
    
    for (const supplier of filteredData) {
      if (!supplierDocMap.has(supplier.name)) {
        supplierDocMap.set(supplier.name, new Map());
      }
      const docMap = supplierDocMap.get(supplier.name)!;
      
      for (const dt of supplier.byDocType || []) {
        const normalizedType = normalizeDocType(dt.type);
        
        // Só considera tipos selecionados
        if (!selectedDocTypes.has(normalizedType)) continue;
        
        const current = docMap.get(normalizedType) || 0;
        // Soma os valores baseado nos status selecionados
        let value = 0;
        if (selectedStatusArray.includes('concluido')) value += dt.concluido || 0;
        if (selectedStatusArray.includes('atrasado')) value += dt.atrasado || 0;
        if (selectedStatusArray.includes('andamento')) value += dt.andamento || 0;
        
        docMap.set(normalizedType, current + value);
      }
    }
    
    // Converte para array e ordena por total
    return Array.from(supplierDocMap.entries())
      .map(([name, docMap]) => {
        const docs: Record<string, number> = {};
        let total = 0;
        for (const [type, count] of docMap.entries()) {
          docs[type] = count;
          total += count;
        }
        // Verifica se é um grupo
        const isGroup = filteredData.find(d => d.name === name)?.isGroup || false;
        return { name, docs, total, isGroup };
      })
      .filter(d => d.total > 0) // Remove fornecedores sem dados
      .sort((a, b) => b.total - a.total);
  }, [filteredData, selectedDocTypes, selectedStatusArray]);

  // Tipos de documento que estão sendo exibidos (ordenados por frequência)
  const displayedDocTypes = useMemo(() => {
    const typeTotals = new Map<string, number>();
    for (const item of chartData) {
      for (const [type, count] of Object.entries(item.docs)) {
        typeTotals.set(type, (typeTotals.get(type) || 0) + count);
      }
    }
    return Array.from(selectedDocTypes)
      .filter(t => typeTotals.has(t))
      .sort((a, b) => (typeTotals.get(b) || 0) - (typeTotals.get(a) || 0));
  }, [chartData, selectedDocTypes]);

  const option: EChartsOption = useMemo(() => {
    if (!chartData || chartData.length === 0) return {};

    // Marca grupos com prefixo para destaque visual
    const categories = chartData.map(d => 
      d.isGroup ? `📁 ${d.name}` : d.name
    );
    
    // Gera uma série para cada tipo de documento
    const series = displayedDocTypes.map((docType, idx) => ({
      name: getDocTypeLabel(docType),
      type: 'bar' as const,
      stack: 'total',
      data: chartData.map(d => d.docs[docType] || 0),
      itemStyle: { 
        color: DOC_TYPE_COLORS[docType] || DOC_TYPE_COLORS.default,
        borderRadius: idx === displayedDocTypes.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0],
      },
      label: {
        show: false,
      },
    }));

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const paramsArray = params as Array<{
            name: string;
            marker: string;
            seriesName: string;
            value: number;
          }>;
          if (!paramsArray.length) return '';
          
          const name = paramsArray[0].name;
          let html = `<strong>${name}</strong><br/>`;
          let total = 0;
          
          for (const p of paramsArray) {
            if (p.value > 0) {
              html += `${p.marker} ${p.seriesName}: <strong>${p.value.toLocaleString('pt-BR')}</strong><br/>`;
              total += p.value;
            }
          }
          
          html += `<br/><strong>Total: ${total.toLocaleString('pt-BR')}</strong>`;
          
          return html;
        },
      },
      legend: {
        top: 0,
        textStyle: { color: textColor },
        data: displayedDocTypes.map(t => getDocTypeLabel(t)),
      },
      grid: {
        left: 12,
        right: 24,
        top: 40,
        bottom: 12,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: { color: textColor },
        splitLine: {
          lineStyle: { color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
        },
      },
      yAxis: {
        type: 'category',
        data: categories,
        inverse: true,
        axisLabel: {
          color: textColor,
          width: 150,
          overflow: 'truncate',
          ellipsis: '...',
        },
      },
      series,
    };
  }, [chartData, displayedDocTypes, textColor, isDark]);

  // Altura dinâmica
  const chartHeight = Math.max(350, Math.min(1000, chartData.length * 40 + 100));

  // Toggle tipo de documento
  const toggleDocType = (type: string) => {
    const newSet = new Set(selectedDocTypes);
    if (newSet.has(type)) {
      newSet.delete(type);
    } else {
      newSet.add(type);
    }
    setSelectedDocTypes(newSet);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[350px]">
        <div className="animate-pulse text-muted-foreground">Carregando gráfico...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[350px] text-muted-foreground">
        Sem dados disponíveis
      </div>
    );
  }

  return (
    <div>
      {/* Filtro de tipos de documento */}
      <div className="flex flex-wrap gap-2 mb-3">
        {availableDocTypes.map(type => (
          <button
            key={type}
            onClick={() => toggleDocType(type)}
            className={cn(
              'px-2 py-1 text-xs rounded-md border transition-colors',
              selectedDocTypes.has(type)
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
            )}
          >
            {getDocTypeLabel(type)}
          </button>
        ))}
      </div>
      
      {/* Info */}
      <div className="text-xs text-muted-foreground mb-2">
        {chartData.length} fornecedor(es) com {displayedDocTypes.map(t => getDocTypeLabel(t)).join(' + ')}
      </div>

      {/* Gráfico */}
      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-[350px] text-muted-foreground">
          Nenhum fornecedor com os tipos selecionados
        </div>
      ) : (
        <div data-pdf-block>
          <ReactECharts
            option={option}
            style={{ height: `${chartHeight}px`, width: '100%' }}
            opts={{ renderer: 'canvas' }}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SEÇÃO PRINCIPAL
// ============================================================================

interface DesempenhoSectionProps {
  className?: string;
}

export function DesempenhoSection({ className }: DesempenhoSectionProps) {
  const { data, isLoading } = useDesempenho();

  const kpis = data?.kpis;
  const bySupplier = data?.bySupplier || [];
  const docTypes = data?.docTypes || [];
  const suppliers = data?.suppliers || [];
  const periods = data?.periods || [];
  const availablePeriods = data?.availablePeriods || [];

  return (
    <section id="desempenho" className={cn('scroll-mt-20', className)}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-8 h-8 text-purple-500" />
        <div>
          <h2 className="text-2xl font-bold">Desempenho</h2>
          <p className="text-sm text-muted-foreground">
            Processos concluídos extraídos do PropertyDocs
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-6">
        <Filters 
          docTypes={docTypes} 
          suppliers={suppliers} 
          periods={periods} 
          availablePeriods={availablePeriods}
          isLoading={isLoading} 
        />
      </div>

      {/* Seletor de Modo de Visualização + Grupos */}
      <div className="mb-6 p-4 bg-muted/30 rounded-lg border">
        <SupplierViewModeSelector availableSuppliers={suppliers} />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <KpiCard
          title="Total Processos"
          value={kpis?.totalProcessos || 0}
          icon={<Briefcase className="w-5 h-5" />}
          color="purple"
          isLoading={isLoading}
        />
        <KpiCard
          title="Concluídos"
          value={kpis?.totalConcluidos || 0}
          icon={<CheckCircle2 className="w-5 h-5" />}
          color="green"
          isLoading={isLoading}
        />
        <KpiCard
          title="Atrasados"
          value={kpis?.totalAtrasados || 0}
          icon={<AlertCircle className="w-5 h-5" />}
          color="red"
          isLoading={isLoading}
        />
        <KpiCard
          title="Em Andamento"
          value={kpis?.totalAndamento || 0}
          icon={<Clock className="w-5 h-5" />}
          color="amber"
          isLoading={isLoading}
        />
        <KpiCard
          title="Fornecedores"
          value={kpis?.totalFornecedores || 0}
          icon={<Users className="w-5 h-5" />}
          color="blue"
          isLoading={isLoading}
        />
        <KpiCard
          title="Documentos"
          value={kpis?.totalDocumentos || 0}
          icon={<FileText className="w-5 h-5" />}
          color="cyan"
          isLoading={isLoading}
        />
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Gráfico 1: Processos por Fornecedor */}
        <div className="bg-card rounded-xl border p-4 shadow-sm">
          <h3 className="text-lg font-semibold mb-2">Processos por Fornecedor</h3>
          <p className="text-xs text-muted-foreground mb-4">
            Total de processos por equipe no período selecionado
          </p>
          <PerformanceChart isLoading={isLoading} />
        </div>

        {/* Gráfico 2: Documentos por Fornecedor */}
        <div className="bg-card rounded-xl border p-4 shadow-sm">
          <h3 className="text-lg font-semibold mb-2">Documentos por Fornecedor</h3>
          <p className="text-xs text-muted-foreground mb-4">
            Processos por tipo de documento (AVCB/Alvará) por fornecedor
          </p>
          <SupplierDocTypeChart data={bySupplier} isLoading={isLoading} />
        </div>
      </div>

      {/* Nota informativa */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground">
        <strong>Fonte:</strong> Dados extraídos do PropertyDocs. Por padrão, mostra processos <strong>Concluídos</strong> e <strong>Atrasados</strong>.
        Use o filtro de Status para incluir processos Em Andamento.
      </div>
    </section>
  );
}
